import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell, faPaperPlane, faClock, faCheckCircle, faTimesCircle,
  faUsers, faChevronDown, faChevronUp, faSpinner, faLightbulb, faCalendarAlt, faBan,
} from "@fortawesome/free-solid-svg-icons";

const BROADCAST_URL = "mst-admin/notifications/broadcast";
const BROADCASTS_LIST_URL = "mst-admin/notifications/broadcasts";
const SEGMENT_COUNT_URL = "mst-admin/notifications/segment-count";
const CANCEL_BROADCAST_URL = (id) => `mst-admin/notifications/broadcast/${id}`;

const SEGMENTS = [
  { value: "ALL",          label: "All Users",           desc: "Every registered match seeker",              color: "#6366F1" },
  { value: "FREE",         label: "Free Users",           desc: "No active paid pack — prime upgrade targets", color: "#64748B" },
  { value: "FREE_OLD",     label: "Free (7d+ old)",       desc: "Free users registered 7+ days ago",           color: "#DC2626" },
  { value: "SPARK_BUNDLE", label: "Spark Bundle",         desc: "Users on spark bundle pack",                  color: "#6366F1" },
  { value: "PREMIUM",      label: "Premium",              desc: "Premium pack holders",                         color: "#7C3AED" },
  { value: "ELITE",        label: "Elite",                desc: "Elite pack holders",                          color: "#F59E0B" },
  { value: "ANNUAL",       label: "Annual",               desc: "Annual pack holders — highest value users",   color: "#059669" },
  { value: "PAID",         label: "All Paid",             desc: "Any active paid pack",                        color: "#2563EB" },
  { value: "INACTIVE_30D", label: "Inactive 30d",         desc: "No activity in 30 days — re-engage",          color: "#EA580C" },
  { value: "NO_ALIGNMENT", label: "No Alignment Unlock",  desc: "Never unlocked deep alignment detail",        color: "#DB2777" },
  { value: "EXPIRING_7D",  label: "Expiring Soon",        desc: "Paid pack expiring in 7 days",                color: "#D97706" },
];

const DEEP_LINKS = [
  { value: "",               label: "None (open app)" },
  { value: "home",           label: "Home / Discovery" },
  { value: "pricing",        label: "Pricing / Upgrade" },
  { value: "subscription",   label: "Subscription Plans" },
  { value: "match-dna",      label: "Match DNA Screen" },
  { value: "astrology",      label: "Astrology / Cosmic Score" },
  { value: "profile",        label: "My Profile" },
  { value: "connections",    label: "Connections" },
];

const NUDGE_TEMPLATES = [
  {
    category: "Upgrade Nudge",
    templates: [
      { title: "Your perfect match is just one unlock away", body: "You've been browsing but haven't unlocked your top matches. Premium gives you 20 full profile reveals. Check what you're missing 👀" },
      { title: "See who's interested in you", body: "3 people viewed your profile today. Upgrade to Premium to see their names and connect instantly. Don't let the right one slip away." },
      { title: "Your match DNA is ready", body: "We've computed your compatibility with 15+ profiles. Unlock Match DNA to see your deepest compatibility scores — available only on Premium." },
    ],
  },
  {
    category: "Re-engagement",
    templates: [
      { title: "New profiles matching your preferences", body: "We found 8 new profiles that match your criteria this week. Come back and explore before they get connected with someone else." },
      { title: "Someone saved your profile", body: "Your profile caught someone's eye. Log in to discover who and send them an interest request today." },
      { title: "Your cosmic compatibility is waiting", body: "Haven't visited in a while? Your Cosmic Score with new matches is ready. See who the stars align you with 🌟" },
    ],
  },
  {
    category: "Annual Pack",
    templates: [
      { title: "Best value — Annual plan now available", body: "Get unlimited browsing, 100 profile unlocks, and MatchMatrix scoring on 12 life factors for a full year. Best investment you'll make for finding your life partner." },
      { title: "Don't let another year pass", body: "Hundreds of matches happen on MatchMatrix every month. Annual pack users find their match 3x faster. Lock in your year today." },
    ],
  },
  {
    category: "Second Marriage",
    templates: [
      { title: "Your second chance at happiness", body: "MatchMatrix's second marriage pool connects you with verified, like-minded individuals who understand your journey. You're not alone." },
    ],
  },
];

const STATUS_ICONS = {
  sent:      { icon: faCheckCircle, cls: "text-green-500" },
  failed:    { icon: faTimesCircle, cls: "text-red-500" },
  scheduled: { icon: faCalendarAlt, cls: "text-indigo-500" },
  draft:     { icon: faClock,       cls: "text-gray-400" },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-";

const SkeletonRow = () => (
  <tr className="border-b border-gray-100 animate-pulse">
    {[...Array(5)].map((_, i) => (
      <td key={i} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
    ))}
  </tr>
);

export default function BroadcastNotifications() {
  const [form, setForm] = useState({ title: "", body: "", deepLink: "", segment: "ALL" });
  const [reachCount, setReachCount] = useState(null);
  const [reachLoading, setReachLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);   // true = "schedule for later"
  const [scheduledAt, setScheduledAt] = useState("");        // ISO datetime-local string

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this scheduled broadcast? This cannot be undone.")) return;
    const res = await func.del(CANCEL_BROADCAST_URL(id));
    if (res?.status === 200) {
      toast.success("Scheduled broadcast cancelled.");
      fetchHistory();
    } else {
      toast.error(res?.data?.message || "Failed to cancel broadcast.");
    }
  };

  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    const res = await func.get(BROADCASTS_LIST_URL);
    if (res?.status === 200) setHistory(res.data?.logs || []);
    setHistLoading(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setReachLoading(true);
      setReachCount(null);
      const res = await func.get(`${SEGMENT_COUNT_URL}?segment=${form.segment}`);
      if (!cancelled && res?.status === 200) setReachCount(res.data?.count ?? null);
      if (!cancelled) setReachLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [form.segment]);

  const setField = (field, val) => setForm((p) => ({ ...p, [field]: val }));

  const applyTemplate = (tpl) => {
    setForm((p) => ({ ...p, title: tpl.title, body: tpl.body }));
    setShowTemplates(false);
  };

  const handleSend = async () => {
    setShowConfirm(false);
    setSending(true);
    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      audience: { segment: form.segment },
    };
    if (form.deepLink) payload.deepLink = form.deepLink;
    if (scheduleMode && scheduledAt) payload.scheduledAt = new Date(scheduledAt).toISOString();

    const res = await func.post(BROADCAST_URL, payload);
    setSending(false);
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.scheduled) {
        toast.success(`Broadcast scheduled for ${new Date(scheduledAt).toLocaleString("en-IN")}`);
      } else {
        toast.success(`Broadcast sent to ${res.data?.sentCount ?? "?"} devices`);
      }
      setForm({ title: "", body: "", deepLink: "", segment: "ALL" });
      setScheduleMode(false);
      setScheduledAt("");
      fetchHistory();
    } else {
      toast.error(res?.data?.message || "Broadcast failed. Check FCM configuration.");
    }
  };

  // Minimum datetime for the picker: 5 minutes from now
  const minDateTime = new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16);

  const canSend = form.title.trim().length > 0 && form.body.trim().length > 0
    && (!scheduleMode || scheduledAt.length > 0);
  const segObj = SEGMENTS.find((s) => s.value === form.segment) || SEGMENTS[0];

  const totalSent = history.reduce((a, b) => a + (b.sentCount || 0), 0);
  const totalBroadcasts = history.length;

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FontAwesomeIcon icon={faBell} className="text-white text-lg" />
          <h1 className="text-xl font-bold text-white">Broadcast Notifications</h1>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          {/* ── Left: Composer (60%) ── */}
          <div className="xl:col-span-3 space-y-4">

            {/* Segment Picker */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Audience Segment</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SEGMENTS.map((seg) => (
                  <button
                    key={seg.value}
                    onClick={() => setField("segment", seg.value)}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition ${
                      form.segment === seg.value
                        ? "border-2 bg-indigo-50"
                        : "border border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                    style={form.segment === seg.value ? { borderColor: seg.color } : {}}
                  >
                    <span className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                    <div>
                      <div className="text-sm font-semibold text-gray-800 leading-tight">{seg.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{seg.desc}</div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Live Reach Count */}
              <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-700">
                <FontAwesomeIcon icon={faUsers} className="text-indigo-400" />
                {reachLoading ? (
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin text-indigo-400" />
                    Calculating reach…
                  </span>
                ) : reachCount !== null ? (
                  <span>You'll reach approximately <strong>{reachCount.toLocaleString()}</strong> users</span>
                ) : (
                  <span className="text-gray-400">Reach count unavailable</span>
                )}
              </div>
            </div>

            {/* Message Compose */}
            <div className="bg-white rounded-xl shadow-md p-5 space-y-4">
              <h2 className="font-semibold text-gray-800">Compose Message</h2>

              {/* Template Picker */}
              <div className="border border-dashed border-indigo-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowTemplates((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition"
                >
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faLightbulb} />
                    Use a template
                  </span>
                  <FontAwesomeIcon icon={showTemplates ? faChevronUp : faChevronDown} className="text-xs" />
                </button>
                {showTemplates && (
                  <div className="p-4 space-y-4 bg-white">
                    {NUDGE_TEMPLATES.map((cat) => (
                      <div key={cat.category}>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{cat.category}</div>
                        <div className="space-y-2">
                          {cat.templates.map((tpl) => (
                            <button
                              key={tpl.title}
                              onClick={() => applyTemplate(tpl)}
                              className="w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition group"
                            >
                              <div className="text-sm font-medium text-gray-800 group-hover:text-indigo-700 leading-tight">{tpl.title}</div>
                              <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{tpl.body}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-medium text-gray-600">Title *</label>
                  <span className={`text-xs ${form.title.length > 55 ? "text-red-500" : "text-gray-400"}`}>
                    {form.title.length}/65
                  </span>
                </div>
                <input
                  type="text"
                  value={form.title}
                  maxLength={65}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="Notification title"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                />
              </div>

              {/* Body */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-medium text-gray-600">Body *</label>
                  <span className={`text-xs ${form.body.length > 180 ? "text-red-500" : "text-gray-400"}`}>
                    {form.body.length}/200
                  </span>
                </div>
                <textarea
                  value={form.body}
                  onChange={(e) => setField("body", e.target.value)}
                  maxLength={200}
                  rows={3}
                  placeholder="Notification message…"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition resize-none"
                />
              </div>

              {/* Deep Link */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Deep Link</label>
                <select
                  value={form.deepLink}
                  onChange={(e) => setField("deepLink", e.target.value)}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition bg-white"
                >
                  {DEEP_LINKS.map((dl) => (
                    <option key={dl.value} value={dl.value}>{dl.label}</option>
                  ))}
                </select>
              </div>

              {/* Schedule toggle */}
              <div className="flex items-center gap-3 px-1">
                <button
                  type="button"
                  onClick={() => { setScheduleMode(false); setScheduledAt(""); }}
                  className={`flex-1 py-2 text-xs font-semibold rounded-lg border transition ${
                    !scheduleMode
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-500 border-gray-300 hover:border-indigo-300"
                  }`}
                >
                  <FontAwesomeIcon icon={faPaperPlane} className="mr-1.5" />
                  Send Now
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleMode(true)}
                  className={`flex-1 py-2 text-xs font-semibold rounded-lg border transition ${
                    scheduleMode
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-500 border-gray-300 hover:border-indigo-300"
                  }`}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-1.5" />
                  Schedule
                </button>
              </div>

              {/* Datetime picker — shown only in schedule mode */}
              {scheduleMode && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Schedule Date & Time</label>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    min={minDateTime}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Time is in your browser's local timezone. Broadcast fires within 5 minutes of this time.
                  </p>
                </div>
              )}

              <button
                disabled={!canSend || sending}
                onClick={() => setShowConfirm(true)}
                className="w-full py-2.5 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon
                  icon={sending ? faSpinner : scheduleMode ? faCalendarAlt : faPaperPlane}
                  className={sending ? "animate-spin" : ""}
                />
                {sending ? (scheduleMode ? "Scheduling…" : "Sending…") : scheduleMode ? "Schedule Broadcast" : "Send Broadcast"}
              </button>
            </div>
          </div>

          {/* ── Right: History (40%) ── */}
          <div className="xl:col-span-2 bg-white rounded-xl shadow-md overflow-hidden flex flex-col">
            {/* Stats bar */}
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800 mb-3">Broadcast History</h2>
              {!histLoading && (
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Total Broadcasts", value: totalBroadcasts },
                    { label: "Total Reach", value: totalSent.toLocaleString() },
                  ].map((s) => (
                    <div key={s.label} className="bg-indigo-50 rounded-lg px-3 py-2 text-center">
                      <div className="text-lg font-bold text-indigo-700">{s.value}</div>
                      <div className="text-xs text-gray-500">{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
              {histLoading ? (
                <table className="w-full text-sm"><tbody>{[...Array(6)].map((_, i) => <SkeletonRow key={i} />)}</tbody></table>
              ) : history.length === 0 ? (
                <div className="py-16 text-center text-gray-400 text-sm">No broadcasts yet</div>
              ) : (
                history.slice(0, 20).map((b) => {
                  const seg = SEGMENTS.find((s) => s.value === (b.audience?.segment || "ALL"));
                  const si = STATUS_ICONS[b.status] || STATUS_ICONS.draft;
                  return (
                    <div key={b._id} className="px-4 py-3 hover:bg-gray-50 transition">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{b.title}</div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span
                              className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                              style={{ backgroundColor: seg?.color || "#6366F1" }}
                            >
                              {seg?.label || b.audience?.segment || "ALL"}
                            </span>
                            <span className="text-xs text-gray-400">{fmtDate(b.createdAt)}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end text-xs gap-1 flex-shrink-0">
                          <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={si.icon} className={si.cls} />
                            <span className="text-gray-500">{b.status || "sent"}</span>
                          </span>
                          {b.status === "scheduled" && b.scheduledAt && (
                            <span className="text-indigo-500 font-medium">{fmtDate(b.scheduledAt)}</span>
                          )}
                          {b.status !== "scheduled" && (
                            <span className="text-green-600 font-semibold">{b.sentCount ?? 0} sent</span>
                          )}
                          {b.failedCount > 0 && (
                            <span className="text-red-500 font-semibold">{b.failedCount} failed</span>
                          )}
                          {b.status === "scheduled" && (
                            <button
                              onClick={() => handleCancel(b._id)}
                              className="flex items-center gap-1 mt-0.5 px-2 py-0.5 text-xs text-red-600 border border-red-300 rounded hover:bg-red-50 transition"
                              title="Cancel scheduled broadcast"
                            >
                              <FontAwesomeIcon icon={faBan} className="text-xs" />
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {scheduleMode ? "Confirm Schedule" : "Confirm Broadcast"}
            </h3>
            <p className="text-sm text-gray-600 mb-1">
              {scheduleMode ? "Schedule" : "Send"} <strong>"{form.title}"</strong> to{" "}
              <strong style={{ color: segObj.color }}>{segObj.label}</strong>?
            </p>
            {scheduleMode && scheduledAt && (
              <p className="text-sm text-indigo-600 font-medium mb-1">
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                Fires at: {new Date(scheduledAt).toLocaleString("en-IN")}
              </p>
            )}
            {!scheduleMode && reachCount !== null && (
              <p className="text-sm text-indigo-600 font-medium mb-1">
                ~{reachCount.toLocaleString()} users will be notified immediately.
              </p>
            )}
            {scheduleMode && reachCount !== null && (
              <p className="text-sm text-gray-500 mb-1">
                ~{reachCount.toLocaleString()} users will be notified at the scheduled time.
              </p>
            )}
            <p className="text-xs text-gray-400 mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-semibold"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
